import { useNavigate } from 'react-router-dom';
import { useAnalyticsState, PRESETS } from '@/analytics/lib';
import { useDialogs } from './dialogs';
import { Icon } from './icons';
import { Badge, Btn, EmptyState, Notice, PageHeading, QueryFailure, TextBtn } from './ui';
import { DeleteReportDialog, SaveReportDialog } from './reportDialogs';
import { modelLabel, useSavedReports } from './data';

const PAGE_PATH = { overview: '/', campaigns: '/campaigns', attribution: '/attribution', traffic: '/traffic' };

// Saved reports — every saved perspective as a card; opening one restores its filters on its screen.
export default function ReportsPage() {
  const [state] = useAnalyticsState();
  const navigate = useNavigate();
  const { openDialog } = useDialogs();
  const reports = useSavedReports(true);
  const open = r => {
    const { page, description, ...params } = r.params || {};
    navigate(`${PAGE_PATH[page] || '/traffic'}?${new URLSearchParams(params)}`);
  };
  const create = () => openDialog({ title: 'Save your perspective', body: <SaveReportDialog state={state} page="overview" /> });
  const list = reports.data || [];
  return <>
    <PageHeading eyebrow="SAVED REPORTS" title="Your perspective, saved" description="Pick up where you left off. Keep the views that matter."
      actions={<Btn primary icon="plus" onClick={create}>Create report</Btn>} />
    {reports.isError ? <QueryFailure onRetry={() => reports.refetch()} busy={reports.isFetching} message="Could not load your saved reports." />
      : <div className="three-columns">
        {reports.isLoading ? <div className="panel empty-state"><p>Loading reports…</p></div>
          : list.length ? list.map(r => (
            <article className="report-card" key={r.id}>
              <div className="between"><span className="report-icon"><Icon name="report" /></span>
                <button type="button" className="btn ghost icon-btn" onClick={() => openDialog({ title: 'Delete saved report?', body: <DeleteReportDialog report={r} /> })} aria-label={`Delete ${r.name}`}><Icon name="close" small /></button></div>
              <h3>{r.name}</h3>
              <p>{r.params?.description || `${modelLabel(r.params?.model || 'last')} attribution · ${r.params?.source || 'All channels'}`}</p>
              <div className="row" style={{ gap: 5, marginTop: 15, flexWrap: 'wrap' }}>
                <Badge>{modelLabel(r.params?.model || 'last')}</Badge>
                <Badge>{PRESETS.find(p => p.id === (r.params?.range || '30d'))?.label || (r.params?.since ? `${r.params.since} – ${r.params.until}` : 'Last 30 days')}</Badge>
                {r.params?.page && r.params.page !== 'overview' && <Badge>{r.params.page}</Badge>}
              </div>
              <div className="report-actions"><span className="muted" style={{ fontSize: 11 }}>Saved {r.created_at ? new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
                <TextBtn icon="arrow" onClick={() => open(r)}>Open report</TextBtn></div>
            </article>))
          : <div className="panel"><EmptyState icon="report" title="A fresh perspective starts here" action={<Btn icon="plus" onClick={create}>Create report</Btn>}>Save your current filters and attribution model as a reusable report.</EmptyState></div>}
      </div>}
    <Notice icon="info" className="" >Reports save the date preset, channel, filters and attribution model of the screen you were on. CSV exports contain the displayed data.</Notice>
  </>;
}
